/**
 * useActivityAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 5 ("Activity Level").
 * Mirrors useLifestyleAnalytics.js exactly — same GA4 wrapper via
 * react-ga4, same IS_PRODUCTION guard, same dev console.group/table logging.
 *
 * Privacy (Nutrition Policy v1.0 §19 Privacy & Data Minimization) —
 * considered and deliberately NOT sanitized: activity tier, exercise
 * types, and frequency are fitness/lifestyle category choices from small
 * fixed enums, not clinically-sensitive answers in the way smoking,
 * alcohol, stress level, and water intake are treated on
 * useLifestyleAnalytics.js (substance use and hydration tied to medical
 * conditions per §16 are the kind of "health answer" §19 is guarding
 * against). Kept raw here for the same reason goal_id and gender are
 * kept raw elsewhere — low-cardinality category data needed for basic
 * funnel/segmentation metrics.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[ActivityAnalytics] ${eventName}`, "color:#00695C;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useActivityAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("activity_step_view", { session_id: sessionId, step: 5 });
  }, []);

  const trackTierSelect = useCallback((sessionId, tierId) => {
    send("activity_tier_select", { session_id: sessionId, tier: tierId });
  }, []);

  const trackExerciseToggle = useCallback((sessionId, exerciseId, selected) => {
    send("activity_exercise_toggle", {
      session_id: sessionId,
      exercise: exerciseId,
      selected,
    });
  }, []);

  const trackFrequencyChange = useCallback((sessionId, field, value) => {
    send("activity_frequency_change", { session_id: sessionId, field, value });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("activity_step_next", {
      session_id: sessionId,
      step: 5,
      activity_tier: data.activityTier,
      exercise_types: Array.isArray(data.exerciseTypes) ? data.exerciseTypes.join(",") : undefined,
      days_per_week: data.daysPerWeek,
      minutes_per_day: data.minutesPerDay,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("activity_step_previous", { session_id: sessionId, step: 5 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("activity_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackTierSelect,
    trackExerciseToggle,
    trackFrequencyChange,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useActivityAnalytics;